import { Schedule } from '@cotera/api';
import {
  RichTextEditor,
  RichTextEditorRef,
} from '@cotera/client/app/components/app/rte';
import { Icon, Title, Tooltip } from '@cotera/client/app/components/ui';
import { NotFoundTab } from '../not-found.tab';
import {
  Alert,
  AlertDescription,
  AlertTitle,
} from '@cotera/client/v0/components/ui/alert';
import {
  AttachmentNode,
  CoteraMention,
  Disclaimer,
  ExpressionNode,
  InputShapeNode,
  JsonTypeBuilderNode,
  MentionDropdownMenu,
} from '@cotera/client/app/components/ui/tiptap/extensions';
import { useMediaQuery } from '@cotera/client/app/hooks';
import {
  MentionData,
  useGraphMentions,
} from '@cotera/client/app/hooks/use-graph-metions';
import {
  PromptInputActionAddAttachments,
  PromptInputActionMenu,
  PromptInputActionMenuContent,
  PromptInputActionMenuTrigger,
  PromptInputMessage,
  PromptInputSubmit,
  PromptInputTools,
} from '@cotera/client/v0/components/ai-elements/prompt-input';
import { Button } from '@cotera/client/v0/components/ui/button';
import { Toggle } from '../components/ui/toggle';
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from '@cotera/client/v0/components/ui/popover';
import {
  scheduleDescription,
  scheduleLabel,
  SENSIBLE_CRON_OPTIONS,
  valueFromSchedule,
} from '@cotera/client/v0/utils';
import { useTrackRecentActivity } from '../hooks/use-track-recent-activity';
import { CronExpression, DG } from '@cotera/sdk/core';
import { Assert } from '@cotera/utilities';
import type { Editor as TiptapEditor } from '@tiptap/react';
import {
  AudioLines,
  Clock,
  ExternalLinkIcon,
  FilterIcon,
  NotepadTextIcon,
  SearchIcon,
  TagsIcon,
  Wrench,
} from 'lucide-react';
import React, { useCallback, useRef } from 'react';
import {
  ModelScope,
  useAction,
  useInScopeModel,
  useWatchableValue,
} from '../actions/framework';
import { Chat, type ChatRef, ChatModel } from '../chat';
import { OpenChatHistoryAction } from '../chat/actions';
import { ModelSelector } from '../chat/input';
import { Suggestion, Suggestions } from '../components/suggestion';
import { AppTab } from '../components/tab/tab';
import { Combobox } from '../components/ui/combobox';
import {
  ResizableHandle,
  ResizablePanel,
  ResizablePanelGroup,
} from '../components/ui/resizable';
import { VoiceRecordingButton } from '../components/voice-recording-button';
import { GraphModel } from '../graph/graph.model';
import { NavigateToMentionAction } from '../graph/navigate-to-mention.action';
import { cn } from '../lib/utils';
import { useTabParams } from '../tab-manager/tab-manager';
import { NewToolAction } from '../tool/new-tool.action';
import { AgentModel } from './agent.model';
import { ChangeAgentModelAction } from './change-agent-model.action';
import { ChangeAgentTabAction } from './change-agent-tab.action';
import { ClearChatAction } from './clear-chat.action';
import { SaveAgentAction } from './save-agent.action';
import { ScheduleAgentAction } from './schedule-agent.action';
import { SubmitChatMessageAction } from './submit-chat-message.action';
import { TEMPLATES } from './templates';
import { VoiceTranscriptionCompleteAction } from './voice-transcription.action';

const LARGE_SCREEN_WIDTH = '(min-width: 1024px)';

const AgentErrorAlert: React.FC = () => {
  const model = useModel();
  const errors = useWatchableValue(model.errors);

  if (errors.length === 0) {
    return null;
  }

  return (
    <Alert variant="destructive" className="mx-6 mt-4 mb-2">
      <Icon icon="warning" />
      <AlertTitle>Agent has errors</AlertTitle>
      <AlertDescription className="flex flex-col gap-2">
        {errors.map((error, index) => (
          <p key={index} className="text-sm">
            {DG.Graph.prettyPrint(error)}
          </p>
        ))}
      </AlertDescription>
    </Alert>
  );
};

const useModel = () => {
  const { id } = useTabParams<{ id: string }>();
  const model = useInScopeModel(AgentModel, id);

  Assert.assert(model !== null, 'Agent Model not found');
  return model;
};

export const NewAgentTab: React.FC = () => {
  const { id } = useTabParams<{ id: string }>();
  const graphModel = useInScopeModel(GraphModel);

  Assert.assert(graphModel !== null);

  const model = React.useMemo(
    () =>
      new AgentModel(
        {
          name: 'New Agent',
          graph: graphModel.graph,
          compiler: graphModel.compiler,
        },
        {
          id,
          isNew: true,
        }
      ),
    [id, graphModel.graph, graphModel.compiler]
  );

  return (
    <ModelScope models={[model]}>
      <AppTab model={model}>
        <AgentPage />
      </AppTab>
    </ModelScope>
  );
};

export const AgentTab: React.FC = () => {
  const { id: name } = useTabParams<{ tabId: string; id: string }>();
  const graphModel = useInScopeModel(GraphModel);

  Assert.assert(graphModel !== null);

  const graph = useWatchableValue(graphModel.graph);

  // Track viewing this agent
  useTrackRecentActivity({ type: 'agent', name });

  const model = React.useMemo(
    () =>
      new AgentModel(
        {
          name,
          graph: graphModel.graph,
          compiler: graphModel.compiler,
        },
        {
          id: name,
        }
      ),
    [name, graphModel.graph, graphModel.compiler]
  );

  if (!graph.hasSymbol(name)) {
    return <NotFoundTab title="Agent Not Found" resource={`Agent "${name}"`} />;
  }

  return (
    <ModelScope models={[model]}>
      <AppTab model={model}>
        <AgentPage />
      </AppTab>
    </ModelScope>
  );
};
type TemplateSuggestion = {
  icon: React.ComponentType<any>;
  text: string;
  description: string;
  color: string;
  templateKey: keyof typeof TEMPLATES;
};

type LinkSuggestion = {
  icon: React.ComponentType<any>;
  text: string;
  description: string;
  color: string;
  href: string;
};

type SuggestionItem = TemplateSuggestion | LinkSuggestion;

const suggestions: SuggestionItem[] = [
  {
    icon: ExternalLinkIcon,
    text: 'View All Templates',
    description: 'Browse our full library of agent templates',
    color: '#6b7280',
    href: 'https://cotera.co/prompts',
  },
  {
    icon: SearchIcon,
    text: 'Google Search',
    description: 'Search the web and extract structured information',
    color: '#76d0eb',
    templateKey: 'Google',
  },
  {
    icon: FilterIcon,
    text: 'Find Mentions',
    description: 'Identify if content mentions specific topics or themes',
    color: '#ea8444',
    templateKey: 'FindMentionsOf',
  },
  {
    icon: NotepadTextIcon,
    text: 'Meeting Summary',
    description: 'Extract key decisions and action items from transcripts',
    color: '#9b87f5',
    templateKey: 'MeetingSummary',
  },
  {
    icon: TagsIcon,
    text: 'Content Classifier',
    description: 'Categorize text into predefined categories',
    color: '#6c71ff',
    templateKey: 'ContentClassifier',
  },
];

const AgentTabs = ({ children }: { children: React.ReactNode }) => {
  return <div className="flex flex-col h-full w-full relative">{children}</div>;
};

const AgentPage: React.FC = () => {
  const model = useModel();
  Assert.assert(model !== null, 'Model not found');
  const showSuggestions = useWatchableValue(model.showSuggestions);
  const identity = useWatchableValue(model.agentIdentity);

  return (
    <AgentTabs key={identity?.toString()}>
      <Header />
      <AgentLayout showSuggestions={showSuggestions} />
    </AgentTabs>
  );
};

const AgentLayout: React.FC<{ showSuggestions?: boolean }> = ({
  showSuggestions,
}) => {
  const isLargeScreen = useMediaQuery(LARGE_SCREEN_WIDTH);

  return (
    <ResizablePanelGroup
      direction={isLargeScreen ? 'horizontal' : 'vertical'}
      className="h-full"
    >
      <Editor
        showSuggestions={showSuggestions}
        className={cn('overflow-y-auto')}
      />

      <EditAndChatResizeHandle />

      <ResizablePanel className="flex-1 flex-grow">
        <AgentTabView />
      </ResizablePanel>
    </ResizablePanelGroup>
  );
};

const EditAndChatResizeHandle: React.FC = () => {
  const model = useModel();
  const currentTab = useWatchableValue(model.currentTab);
  const isLargeScreen = useMediaQuery(LARGE_SCREEN_WIDTH);
  const isDisabled = currentTab !== 'edit-and-chat';

  return (
    <ResizableHandle
      className={cn(
        'ml-4 transition-opacity duration-200',
        isDisabled && 'opacity-0 pointer-events-none'
      )}
      withHandle={!isLargeScreen && !isDisabled}
      disabled={isDisabled}
    />
  );
};

const scheduleOptions = [
  { value: CronExpression.EVERY_MINUTE, label: 'Every minute' },
  { value: 'hourly', label: 'Hourly' },
  { value: 'daily', label: 'Daily' },
  { value: 'never', label: 'Never' },
  ...SENSIBLE_CRON_OPTIONS.map(([k]) => ({
    value: k,
    label: scheduleLabel(k),
  })),
];

const AgentTitle: React.FC<{
  className?: string;
  containerClassName?: string;
}> = ({ className, containerClassName }) => {
  const model = useModel();
  const hasMessages = useWatchableValue(model.hasMessages);
  const currentTab = useWatchableValue(model.currentTab);
  const name = useWatchableValue(model.name);

  return (
    <div
      className={cn(
        'flex-1 h-full items-center overflow-hidden',
        containerClassName
      )}
    >
      <div className="flex-1 max-w-4xl mx-auto mx-2">
        <Title
          type="title"
          className={cn(
            '!font-[700] transition-all duration-500 ease-out overflow-hidden text-ellipsis whitespace-nowrap',
            hasMessages && currentTab === 'chat' ? 'opacity-100' : 'opacity-0 ',
            className
          )}
        >
          {name}
        </Title>
      </div>
    </div>
  );
};

const Header: React.FC = () => {
  const saveAction = useAction(SaveAgentAction);
  const scheduleAction = useAction<{ schedule?: Schedule }>(
    ScheduleAgentAction
  );
  const newToolAction = useAction(NewToolAction);
  const model = useModel();
  const formState = useWatchableValue(model.formState);
  const currentTab = useWatchableValue(model.currentTab);
  const changeAgentTabAction = useAction(ChangeAgentTabAction);
  const clearChatAction = useAction(ClearChatAction);

  const activeClassName = 'bg-white! text-foreground!';

  const handleScheduleChange = useCallback(
    async (value: string) => {
      // Convert the value to the proper Schedule type
      let schedule: Schedule;
      if (value === 'hourly' || value === 'daily' || value === 'never') {
        schedule = value;
      } else {
        schedule = { cron: value };
      }
      await scheduleAction.execute({ schedule });
      // Note: setWhen will be called by the useEffect below when formState.schedule updates
    },
    [scheduleAction]
  );

  return (
    <div className="w-full sticky top-0 bg-background z-10">
      <div className="flex justify-between px-4 pt-2.5 pb-2 items-center">
        <div className="flex space-x-2 items-center">
          <div className="flex space-x-2 bg-gray-100 rounded-md p-1">
            <Button
              variant="ghost"
              size={'sm'}
              className={currentTab === 'edit-and-chat' ? activeClassName : ''}
              onClick={async () => {
                await changeAgentTabAction.execute({ tab: 'edit-and-chat' });
              }}
            >
              <Icon icon="edit" />
              Edit Agent
            </Button>
            <Button
              variant="ghost"
              size={'sm'}
              className={currentTab === 'chat' ? activeClassName : ''}
              onClick={async () => {
                await changeAgentTabAction.execute({ tab: 'chat' });
              }}
            >
              <Icon icon="message-square" />
              Chat with Agent
            </Button>
          </div>
          {currentTab === 'edit-and-chat' && (
            <Button
              variant="outline"
              size="sm"
              onClick={() => newToolAction.execute()}
            >
              <Wrench className="h-4 w-4" />
              Install Tools
            </Button>
          )}
          <Tooltip tooltipContent="New chat" side="bottom">
            <Button
              variant="outline"
              className="rounded-full"
              size="icon"
              onClick={() => void clearChatAction.execute({})}
            >
              <Icon icon="plus" />
            </Button>
          </Tooltip>
        </div>
        <AgentTitle
          containerClassName="hidden sm:flex items-center justify-start"
          className="px-4"
        />
        <div className="flex space-x-4">
          <Popover>
            <PopoverTrigger asChild>
              <Button variant="ghost">
                <Clock className="text-muted-foreground" />
                <span className="text-muted-foreground">Schedule</span>
              </Button>
            </PopoverTrigger>
            <PopoverContent className="flex flex-col gap-4">
              <div className="flex flex-col gap-2 w-full">
                <Combobox
                  value={valueFromSchedule(formState.schedule)}
                  items={scheduleOptions}
                  onChange={async (v) => {
                    await handleScheduleChange(v);
                  }}
                />
                <div className="text-sm text-muted-foreground">
                  {scheduleDescription(formState.schedule, 'agent')}
                </div>
              </div>
            </PopoverContent>
          </Popover>
          <Button variant="outline" onClick={() => saveAction.execute()}>
            <Icon icon="save" className="text-muted-foreground" />
            <span className="text-muted-foreground">Save</span>
          </Button>
        </div>
      </div>
      <AgentTitle containerClassName="sm:hidden" className="px-4 pt-2" />
    </div>
  );
};

const Editor: React.FC<{
  showSuggestions?: boolean;
  className?: string;
}> = ({ showSuggestions, className }) => {
  const model = useModel();
  const { agentState } = useWatchableValue(model.formState);
  const navigateToMentionAction = useAction(NavigateToMentionAction);
  const currentTab = useWatchableValue(model.currentTab);
  const graphModel = useInScopeModel(GraphModel);

  const { options: mentionOptions, groups: mentionGroups } = useGraphMentions({
    useModel,
    types: ['document', 'expr'],
  });

  const makePlaceholder = ({
    editor,
    node,
  }: {
    editor: TiptapEditor;
    node: any;
  }): string => {
    // Check if current node is an h1 heading
    const isH1 = node.type.name === 'heading' && node.attrs['level'] === 1;

    if (isH1 && node.textContent.trim() === '') {
      return 'Give your agent a name...';
    }

    // Check if there's already body text (not counting the heading)
    const doc = editor.state.doc;
    let hasBodyText = false;

    doc.descendants((node, _pos) => {
      // Skip the heading
      if (node.type.name === 'heading' && node.attrs['level'] === 1) {
        return true;
      }
      // Check if there's any text content in other nodes
      if (node.isText && node.text && node.text.trim().length > 0) {
        hasBodyText = true;
        return false; // Stop searching
      }
      return true;
    });

    // Fun placeholder variations when there's already content
    if (hasBodyText) {
      const funPlaceholders = [
        'Tell me more...',
        'What else should I know?',
        'Keep going...',
        'And then?',
        "What's next?",
        'Continue your thought...',
      ];

      // Use a simple hash of the content length to pick a consistent placeholder
      const contentLength = editor.state.doc.textContent.length;
      const index = contentLength % funPlaceholders.length;
      return funPlaceholders[index] ?? 'Tell me more...';
    }

    return "Start writing your Agent's prompt...";
  };

  const editorRef = useRef<RichTextEditorRef>(null);

  const handleMentionClick = useCallback(
    async (data: MentionData) => {
      await navigateToMentionAction.execute({
        mentionType: data.t as 'tool' | 'document' | 'expr',
        mentionName: data.name,
        toolKey: data.name,
      });
    },
    [navigateToMentionAction]
  );

  return (
    <ResizablePanel
      className={cn(
        'relative',
        currentTab === 'chat' ? '!w-0 !h-0 !overflow-hidden !flex-none' : ''
      )}
    >
      <div
        className={cn(
          'transition-all duration-500 ease-in-out w-full md:max-w-5xl mx-auto relative shrink-0',
          'shadow-none h-full flex flex-col',
          'pt-5',
          currentTab === 'chat' ? '!w-0 !h-0 !overflow-hidden' : 'w-full',
          className
        )}
      >
        <AgentErrorAlert />
        <div className="flex-1 min-h-0">
          <RichTextEditor
            ref={editorRef}
            className={cn('h-full', showSuggestions ? 'pb-6 md:pb-8' : '')}
            titlePlaceholder="Give your agent a name..."
            placeholder={makePlaceholder}
            extensions={[
              {
                extension: Disclaimer,
                config: {
                  menus: ['turn-into', 'drag-context', 'slash'],
                },
              },
              {
                extension: InputShapeNode,
                config: {
                  menus: ['slash'],
                },
              },
              {
                extension: JsonTypeBuilderNode,
                config: {
                  menus: ['slash'],
                },
              },
              {
                extension: ExpressionNode.configure({
                  parser: graphModel?.graph.snapshot().parser,
                }),
                config: {
                  menus: ['slash'],
                },
              },
              {
                extension: AttachmentNode.configure({
                  items: [],
                }),
                config: {
                  menus: ['slash'],
                },
              },
              {
                extension: CoteraMention.configure({
                  onClick: handleMentionClick,
                }),
              },
            ]}
            content={agentState}
            onUpdate={(editor) => {
              model.formState.set({
                ...model.formState.snapshot(),
                agentState: editor.getJSON(),
              });
            }}
          >
            <MentionDropdownMenu
              options={mentionOptions}
              groups={mentionGroups}
            />
          </RichTextEditor>
        </div>
        {showSuggestions && (
          <div className="mt-auto w-full">
            <div className="relative bg-background/95 pt-4 pb-6 sm:pb-7 supports-[backdrop-filter]:bg-background/75 supports-[backdrop-filter]:backdrop-blur">
              <Suggestions className="w-full px-6 sm:px-10">
                {suggestions.map((suggestion) => {
                  const { icon: Icon, text, color, description } = suggestion;
                  const isLink = 'href' in suggestion;

                  if (isLink) {
                    return (
                      <Tooltip
                        key={text}
                        tooltipContent={description}
                        side="top"
                      >
                        <a
                          href={suggestion.href}
                          target="_blank"
                          rel="noopener noreferrer"
                        >
                          <Suggestion className="font-normal" suggestion={text}>
                            {Icon !== undefined && (
                              <Icon size={16} style={{ color }} />
                            )}
                            {text}
                          </Suggestion>
                        </a>
                      </Tooltip>
                    );
                  }

                  return (
                    <Tooltip key={text} tooltipContent={description} side="top">
                      <Suggestion
                        className="font-normal"
                        onClick={() => {
                          const template = TEMPLATES[suggestion.templateKey];
                          if (editorRef.current?.editor) {
                            const templateState =
                              new DG.DocumentEditor.EditorState(template);
                            const newState = templateState.setTitle(
                              templateState
                                .title()
                                ?.replace('Template: ', '') ?? ''
                            );
                            editorRef.current.editor.commands.setContent(
                              newState.getState(),
                              {
                                emitUpdate: true,
                              }
                            );
                          }
                        }}
                        suggestion={text}
                      >
                        {Icon !== undefined && (
                          <Icon size={16} style={{ color }} />
                        )}
                        {text}
                      </Suggestion>
                    </Tooltip>
                  );
                })}
              </Suggestions>
            </div>
          </div>
        )}
      </div>
    </ResizablePanel>
  );
};

const AgentChatInputFooter = ({
  includeModelSelector,
  chatRef,
  onRecordingStateChange,
}: {
  includeModelSelector: boolean;
  chatRef: React.RefObject<ChatRef>;
  onRecordingStateChange?: (recording: boolean) => void;
}) => {
  const agentModel = useModel();
  const { model } = useWatchableValue(agentModel.formState);
  const historyAction = useAction(OpenChatHistoryAction);
  const changeModelAction = useAction(ChangeAgentModelAction);
  const voiceTranscriptionAction = useAction(VoiceTranscriptionCompleteAction);
  const chatModel = useInScopeModel(ChatModel);
  const yoloMode = useWatchableValue(chatModel.yoloModeWatchable);

  return (
    <div className="flex justify-between items-center w-full px-1">
      <PromptInputTools>
        <PromptInputActionMenu>
          <PromptInputActionMenuTrigger />
          <PromptInputActionMenuContent>
            <PromptInputActionAddAttachments />
          </PromptInputActionMenuContent>
        </PromptInputActionMenu>
        <Tooltip tooltipContent="Conversation history" side="top">
          <Button
            variant="ghost"
            size="icon-sm"
            aria-label="Open conversation history"
            type="button"
            onClick={() => {
              void historyAction.execute({});
            }}
          >
            <Icon icon="history" className="text-muted-foreground" />
          </Button>
        </Tooltip>
        <VoiceRecordingButton
          onTranscriptionComplete={async (text) =>
            await voiceTranscriptionAction.execute({
              transcribedText: text,
              chatRef,
            })
          }
          onRecordingStateChange={onRecordingStateChange}
        />
        <Tooltip tooltipContent="YOLO Mode - Auto-approve all tools" side="top">
          <Toggle
            pressed={yoloMode}
            onPressedChange={(pressed) => chatModel.setYoloMode(pressed)}
            size="sm"
            variant="outline"
            aria-label="Toggle YOLO mode"
          >
            <Icon icon="zap" className={cn(yoloMode && 'text-orange-500')} />
          </Toggle>
        </Tooltip>
      </PromptInputTools>

      {includeModelSelector && (
        <ModelSelector
          model={model}
          handleModelChange={async (model) =>
            await changeModelAction.execute({ model })
          }
        />
      )}
    </div>
  );
};

const CenteredChatWrapper: React.FC<{
  children: React.ReactNode;
}> = ({ children }) => {
  const model = useModel();
  const hasMessages = useWatchableValue(model.hasMessages);
  const currentTab = useWatchableValue(model.currentTab);
  const isLargeScreen = useMediaQuery(LARGE_SCREEN_WIDTH);
  const showTitle = !hasMessages && currentTab === 'chat';
  const showCentered = !hasMessages && currentTab === 'chat' && isLargeScreen;
  const [isTransitioning, setIsTransitioning] = React.useState(false);
  const hasTransitionedRef = React.useRef(false);
  const name = useWatchableValue(model.name);

  React.useEffect(() => {
    // Only trigger transition ONCE when messages first appear on chat tab
    if (
      hasMessages &&
      currentTab === 'chat' &&
      showCentered === false &&
      isLargeScreen &&
      !hasTransitionedRef.current
    ) {
      hasTransitionedRef.current = true;
      setIsTransitioning(true);
      const timer = setTimeout(() => setIsTransitioning(false), 300);
      return () => clearTimeout(timer);
    }
    return undefined;
  }, [hasMessages, currentTab, showCentered, isLargeScreen]);

  return (
    <div
      className={cn(
        'agent-chat-container transition-all duration-300 ease-out px-4',
        showCentered
          ? 'agent-chat-centered h-full w-full flex flex-col items-center justify-center px-4'
          : 'h-full w-full flex flex-col min-h-0',
        isTransitioning && 'agent-chat-transitioning'
      )}
    >
      {showTitle && (
        <div className="mb-8 text-center transition-all duration-300 ease-in-out opacity-100 translate-y-0 shrink-0">
          <h2 className="text-2xl font-semibold mb-2">
            {name ? `Chat with ${name}` : 'Start a conversation'}
          </h2>
          <p className="text-muted-foreground">
            Ask me anything and I'll help you out
          </p>
        </div>
      )}
      <div
        className={cn(
          'w-full transition-all duration-300 ease-out px-2',
          showCentered ? 'max-w-4xl px-2' : 'flex-1 min-h-0 overflow-y-auto'
        )}
      >
        {children}
      </div>
    </div>
  );
};

const AgentTabView: React.FC = () => {
  const model = useModel();
  const currentTab = useWatchableValue(model.currentTab);
  const includeModelSelector = currentTab === 'edit-and-chat' || model.isNew;
  const identity = useWatchableValue(model.agentIdentity);
  const chatId = useWatchableValue(model.chatId);
  const promptInput = useWatchableValue(model.promptInput);
  const chatRef = React.useRef<ChatRef>(null);
  const [isRecording, setIsRecording] = React.useState(false);
  const submitMessageAction = useAction(SubmitChatMessageAction);
  const clearChatAction = useAction(ClearChatAction);
  const needsRestore = useWatchableValue(model.needsMessageRestore);

  const handleRecordingStateChange = React.useCallback((recording: boolean) => {
    setIsRecording(recording);
  }, []);

  const handleClearChat = React.useCallback(() => {
    void clearChatAction.execute({});
  }, [clearChatAction]);

  const handleSubmit = async (message: PromptInputMessage) => {
    let text: string;
    if (message.type === 'text') {
      text = message.text;
    } else {
      const editor = new DG.DocumentEditor.EditorState(
        message.editorState as DG.DocumentEditor.DocumentState
      );
      text = editor.toPromptMarkdown(DG.BuildExplorer.empty(), 'simple');
    }
    await submitMessageAction.execute({ message: text });
  };

  const handleMessagesRestored = React.useCallback(
    (messageCount: number) => {
      model.hasMessages.set(messageCount > 0);
      model.needsMessageRestore.set(false);
    },
    [model]
  );

  return (
    <CenteredChatWrapper>
      <Chat
        key={chatId}
        agent={identity?.toString() ?? null}
        ref={chatRef}
        parent={model}
        chatId={chatId}
        systemMessage={promptInput.systemMessage}
        model={promptInput.model}
        toolIds={promptInput.toolIds}
        outputShape={promptInput.outputShape}
        restoreFromServer={needsRestore}
        onMessagesRestored={handleMessagesRestored}
        inputFooter={
          <AgentChatInputFooter
            includeModelSelector={includeModelSelector ?? false}
            chatRef={chatRef}
            onRecordingStateChange={handleRecordingStateChange}
          />
        }
        customSubmitButton={
          isRecording ? (
            <PromptInputSubmit
              size="icon-sm"
              disabled
              className="bg-gray-200 text-gray-800"
            >
              <AudioLines className="size-4 animate-pulse-scale" />
            </PromptInputSubmit>
          ) : undefined
        }
        onSubmit={handleSubmit}
        onClearChat={handleClearChat}
      />
    </CenteredChatWrapper>
  );
};
